import {
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
} from "@tanstack/react-router";
import { lazy } from "react";
import { RequirePermissions } from "@/modules/auth/presentation/ui/RequirePermissions";
import { parseKpiOverviewExplainSearch } from "@/modules/kpi/presentation/utils/kpi-overview-explain-search";
import { qiCreatePrefillValidateSearch } from "@/modules/quotation-invoice/presentation/lib/qi-create-prefill";
import { Permissions as AppPermissions } from "@/modules/roles/domain/contracts/permissions";
import { LazyPage } from "@/shared/ui/LazyPage";

const RootLayout = lazy(() =>
  import("./layout/RootLayout").then((module) => ({
    default: module.RootLayout,
  })),
);

const EmployeeProfileListPage = lazy(() =>
  import(
    "@/modules/hr/employee-profile/presentation/pages/EmployeeProfileListPage"
  ).then((module) => ({ default: module.EmployeeProfileListPage })),
);
const EmployeeProfileDetailPage = lazy(() =>
  import(
    "@/modules/hr/employee-profile/presentation/pages/EmployeeProfileDetailPage"
  ).then((module) => ({ default: module.EmployeeProfileDetailPage })),
);
const SalaryAdvanceListPage = lazy(() =>
  import(
    "@/modules/hr/salary-advance/presentation/pages/SalaryAdvanceListPage"
  ).then((module) => ({ default: module.SalaryAdvanceListPage })),
);
const SalaryAdvanceDetailPage = lazy(() =>
  import(
    "@/modules/hr/salary-advance/presentation/pages/SalaryAdvanceDetailPage"
  ).then((module) => ({ default: module.SalaryAdvanceDetailPage })),
);
const MyAdvanceListPage = lazy(() =>
  import(
    "@/modules/hr/salary-advance/presentation/pages/MyAdvanceListPage"
  ).then((module) => ({ default: module.MyAdvanceListPage })),
);
const NewAdvanceRequestPage = lazy(() =>
  import(
    "@/modules/hr/salary-advance/presentation/pages/NewAdvanceRequestPage"
  ).then((module) => ({ default: module.NewAdvanceRequestPage })),
);
const MyAdvanceDetailPage = lazy(() =>
  import(
    "@/modules/hr/salary-advance/presentation/pages/MyAdvanceDetailPage"
  ).then((module) => ({ default: module.MyAdvanceDetailPage })),
);
const NewAdvanceForEmployeePage = lazy(() =>
  import(
    "@/modules/hr/salary-advance/presentation/pages/NewAdvanceForEmployeePage"
  ).then((module) => ({ default: module.NewAdvanceForEmployeePage })),
);
const ErrorBoundary = lazy(() =>
  import("./error/ErrorBoundary").then((module) => ({
    default: module.ErrorBoundary,
  })),
);
const AuthLayout = lazy(() =>
  import("./layout/AuthLayout").then((module) => ({
    default: module.AuthLayout,
  })),
);

const LoginPage = lazy(() =>
  import("@/modules/auth/presentation/pages/LoginPage").then((module) => ({
    default: module.LoginPage,
  })),
);
const AuthenticatedLayout = lazy(() =>
  import("./layout/AuthenticatedLayout").then((module) => ({
    default: module.AuthenticatedLayout,
  })),
);
const DashboardPage = lazy(() =>
  import("@/modules/dashboard/presentation/pages/DashboardPage").then(
    (module) => ({
      default: module.DashboardPage,
    }),
  ),
);
const RolesPage = lazy(() =>
  import("@/modules/roles/presentation/pages/RolesPage").then((module) => ({
    default: module.RolesPage,
  })),
);
const RoleCreatePage = lazy(() =>
  import("@/modules/roles/presentation/pages/RoleCreatePage").then(
    (module) => ({
      default: module.RoleCreatePage,
    }),
  ),
);
const RoleEditPage = lazy(() =>
  import("@/modules/roles/presentation/pages/RoleEditPage").then((module) => ({
    default: module.RoleEditPage,
  })),
);
// const AuditPage = lazy(() =>
//   import("@/modules/audit/presentation/pages/AuditPage").then((module) => ({
//     default: module.AuditPage,
//   })),
// );
// const AuditDetailPage = lazy(() =>
//   import("@/modules/audit/presentation/pages/AuditDetailPage").then(
//     (module) => ({
//       default: module.AuditDetailPage,
//     }),
//   ),
// );
const UsersPage = lazy(() =>
  import("@/modules/users/presentation/pages/UsersPage").then((module) => ({
    default: module.UsersPage,
  })),
);
const UserProfilePage = lazy(() =>
  import("@/modules/users/presentation/pages/UserProfilePage").then(
    (module) => ({
      default: module.UserProfilePage,
    }),
  ),
);
const PositionsPage = lazy(() =>
  import("@/modules/positions/presentation/pages/PositionsPage").then(
    (module) => ({
      default: module.PositionsPage,
    }),
  ),
);
const DepartmentsPage = lazy(() =>
  import("@/modules/departments/presentation/pages/DepartmentsPage").then(
    (module) => ({
      default: module.DepartmentsPage,
    }),
  ),
);
const SOPPage = lazy(() =>
  import("@/modules/sop/presentation/pages/SOPPage").then((module) => ({
    default: module.SOPPage,
  })),
);
const SOPCreatePage = lazy(() =>
  import("@/modules/sop/presentation/pages/SOPCreatePage").then((module) => ({
    default: module.SOPCreatePage,
  })),
);
const SOPEditPage = lazy(() =>
  import("@/modules/sop/presentation/pages/SOPEditPage").then((module) => ({
    default: module.SOPEditPage,
  })),
);
const SOPDetailPage = lazy(() =>
  import("@/modules/sop/presentation/pages/SOPDetailPage").then((module) => ({
    default: module.SOPDetailPage,
  })),
);
const SOPPdfPreviewPage = lazy(() =>
  import("@/modules/sop/presentation/pages/SOPPdfPreviewPage").then(
    (module) => ({
      default: module.SOPPdfPreviewPage,
    }),
  ),
);
const SOPMappingPage = lazy(() =>
  import("@/modules/sop/presentation/pages/SOPMappingPage").then((module) => ({
    default: module.SOPMappingPage,
  })),
);
const OrgChartOverviewPage = lazy(() =>
  import("@/modules/org-chart/presentation/pages/OrgChartOverviewPage").then(
    (module) => ({
      default: module.OrgChartOverviewPage,
    }),
  ),
);
const AssignPositionPage = lazy(() =>
  import(
    "@/modules/assign-position/presentation/pages/AssignPositionPage"
  ).then((module) => ({
    default: module.AssignPositionPage,
  })),
);
const ProfilePage = lazy(() =>
  import("@/modules/auth/presentation/pages/ProfilePage").then((module) => ({
    default: module.ProfilePage,
  })),
);
const JDListPage = lazy(() =>
  import("@/modules/jd/presentation/pages/JDListPage").then((module) => ({
    default: module.JDListPage,
  })),
);
const JDDetailPage = lazy(() =>
  import("@/modules/jd/presentation/pages/JDDetailPage").then((module) => ({
    default: module.JDDetailPage,
  })),
);
const JDPdfPreviewPage = lazy(() =>
  import("@/modules/jd/presentation/pages/JDPdfPreviewPage").then((module) => ({
    default: module.JDPdfPreviewPage,
  })),
);
const JDCreatePage = lazy(() =>
  import("@/modules/jd/presentation/pages/JDCreatePage").then((module) => ({
    default: module.JDCreatePage,
  })),
);
const JDMappingPage = lazy(() =>
  import("@/modules/jd-mapping/presentation/pages/JDMappingPage").then(
    (module) => ({
      default: module.JDMappingPage,
    }),
  ),
);
const Forbidden = lazy(() =>
  import("./error/Forbidden").then((module) => ({
    default: module.Forbidden,
  })),
);
const ChecklistsPage = lazy(() =>
  import("@/modules/checklist/presentation/pages/ChecklistsPage").then(
    (module) => ({
      default: module.ChecklistsPage,
    }),
  ),
);
const ChecklistDetailPage = lazy(() =>
  import("@/modules/checklist/presentation/pages/ChecklistDetailPage").then(
    (module) => ({
      default: module.ChecklistDetailPage,
    }),
  ),
);
const ChecklistCreatePage = lazy(() =>
  import("@/modules/checklist/presentation/pages/ChecklistCreatePage").then(
    (module) => ({
      default: module.ChecklistCreatePage,
    }),
  ),
);
const ChecklistEditPage = lazy(() =>
  import("@/modules/checklist/presentation/pages/ChecklistEditPage").then(
    (module) => ({
      default: module.ChecklistEditPage,
    }),
  ),
);
const ChecklistExecutionHistoryPage = lazy(() =>
  import(
    "@/modules/checklist/presentation/pages/ChecklistExecutionHistoryPage"
  ).then((module) => ({
    default: module.ChecklistExecutionHistoryPage,
  })),
);
const ChecklistExecutionDetailPage = lazy(() =>
  import(
    "@/modules/checklist/presentation/pages/ChecklistExecutionDetailPage"
  ).then((module) => ({
    default: module.ChecklistExecutionDetailPage,
  })),
);
const ChecklistResultPage = lazy(() =>
  import("@/modules/checklist/presentation/pages/ChecklistResultPage").then(
    (module) => ({
      default: module.ChecklistResultPage,
    }),
  ),
);
const FlowsPage = lazy(() =>
  import("@/modules/flow/presentation/pages/FlowsPage").then((module) => ({
    default: module.FlowsPage,
  })),
);
const FlowDetailPage = lazy(() =>
  import("@/modules/flow/presentation/pages/FlowDetailPage").then((module) => ({
    default: module.FlowDetailPage,
  })),
);
const FlowEditPage = lazy(() =>
  import("@/modules/flow/presentation/pages/FlowEditorPage").then((module) => ({
    default: module.FlowEditorPage,
  })),
);
const QCListPage = lazy(() =>
  import("@/modules/qc/presentation/pages/QCListPage").then((module) => ({
    default: module.QCListPage,
  })),
);
const QCDetailPage = lazy(() =>
  import("@/modules/qc/presentation/pages/QCDetailPage").then((module) => ({
    default: module.QCDetailPage,
  })),
);
const QCPdfPreviewPage = lazy(() =>
  import("@/modules/qc/presentation/pages/QCPdfPreviewPage").then((module) => ({
    default: module.QCPdfPreviewPage,
  })),
);
const QCCreatePage = lazy(() =>
  import("@/modules/qc/presentation/pages/QCCreatePage").then((module) => ({
    default: module.QCCreatePage,
  })),
);
const QCEditPage = lazy(() =>
  import("@/modules/qc/presentation/pages/QCEditPage").then((module) => ({
    default: module.QCEditPage,
  })),
);
const QCRecordPage = lazy(() =>
  import("@/modules/qc/presentation/pages/QCRecordPage").then((module) => ({
    default: module.QCRecordPage,
  })),
);
const QCRecordListPage = lazy(() =>
  import("@/modules/qc/presentation/pages/QCRecordListPage").then((module) => ({
    default: module.QCRecordListPage,
  })),
);
const QCRecordDetailPage = lazy(() =>
  import("@/modules/qc/presentation/pages/QCRecordDetailPage").then(
    (module) => ({
      default: module.QCRecordDetailPage,
    }),
  ),
);
const ServiceStandardListPage = lazy(() =>
  import(
    "@/modules/service-standard/presentation/pages/ServiceStandardListPage"
  ).then((module) => ({
    default: module.ServiceStandardListPage,
  })),
);
const ServiceStandardDetailPage = lazy(() =>
  import(
    "@/modules/service-standard/presentation/pages/ServiceStandardDetailPage"
  ).then((module) => ({
    default: module.ServiceStandardDetailPage,
  })),
);
const ServiceStandardCreatePage = lazy(() =>
  import(
    "@/modules/service-standard/presentation/pages/ServiceStandardCreatePage"
  ).then((module) => ({
    default: module.ServiceStandardCreatePage,
  })),
);
const ServiceStandardEditPage = lazy(() =>
  import(
    "@/modules/service-standard/presentation/pages/ServiceStandardEditPage"
  ).then((module) => ({
    default: module.ServiceStandardEditPage,
  })),
);
const ServiceStandardPdfPreviewPage = lazy(() =>
  import(
    "@/modules/service-standard/presentation/pages/ServiceStandardPdfPreviewPage"
  ).then((module) => ({
    default: module.ServiceStandardPdfPreviewPage,
  })),
);
const ServiceStandardMappingPage = lazy(() =>
  import(
    "@/modules/service-standard/presentation/pages/ServiceStandardMappingPage"
  ).then((module) => ({
    default: module.ServiceStandardMappingPage,
  })),
);
const TemplateListPage = lazy(() =>
  import("@/modules/template/presentation/pages/TemplateListPage").then(
    (module) => ({
      default: module.TemplateListPage,
    }),
  ),
);
const TemplateDetailPage = lazy(() =>
  import("@/modules/template/presentation/pages/TemplateDetailPage").then(
    (module) => ({
      default: module.TemplateDetailPage,
    }),
  ),
);
const TemplateCreatePage = lazy(() =>
  import("@/modules/template/presentation/pages/TemplateCreatePage").then(
    (module) => ({
      default: module.TemplateCreatePage,
    }),
  ),
);
const TemplateByDepartmentPage = lazy(() =>
  import("@/modules/template/presentation/pages/TemplateByDepartmentPage").then(
    (module) => ({
      default: module.TemplateByDepartmentPage,
    }),
  ),
);
const QCRecordPdfPreviewPage = lazy(() =>
  import("@/modules/qc/presentation/pages/QCRecordPdfPreviewPage").then(
    (module) => ({
      default: module.QCRecordPdfPreviewPage,
    }),
  ),
);
const QCResultPage = lazy(() =>
  import("@/modules/qc/presentation/pages/QCResultPage").then((module) => ({
    default: module.QCResultPage,
  })),
);
const KPIOverviewPage = lazy(() =>
  import("@/modules/kpi/presentation/pages/KPIOverviewPage").then((module) => ({
    default: module.KPIOverviewPage,
  })),
);
const KPIOverviewExplainPage = lazy(() =>
  import("@/modules/kpi/presentation/pages/KPIOverviewExplainPage").then(
    (module) => ({
      default: module.KPIOverviewExplainPage,
    }),
  ),
);
const KPIsPage = lazy(() =>
  import("@/modules/kpi/presentation/pages/KPIsPage").then((module) => ({
    default: module.KPIsPage,
  })),
);
const KPICreatePage = lazy(() =>
  import("@/modules/kpi/presentation/pages/KPICreatePage").then((module) => ({
    default: module.KPICreatePage,
  })),
);
const KPIEditPage = lazy(() =>
  import("@/modules/kpi/presentation/pages/KPIEditPage").then((module) => ({
    default: module.KPIEditPage,
  })),
);
const KPIDetailPage = lazy(() =>
  import("@/modules/kpi/presentation/pages/KPIDetailPage").then((module) => ({
    default: module.KPIDetailPage,
  })),
);
const KPIPdfPreviewPage = lazy(() =>
  import("@/modules/kpi/presentation/pages/KPIPdfPreviewPage").then(
    (module) => ({
      default: module.KPIPdfPreviewPage,
    }),
  ),
);
const KPICalculationPage = lazy(() =>
  import("@/modules/kpi/presentation/pages/KPICalculationPage").then(
    (module) => ({
      default: module.KPICalculationPage,
    }),
  ),
);
const AuditPlanListPage = lazy(() =>
  import("@/modules/internal-audit/presentation/pages/AuditPlanListPage").then(
    (module) => ({
      default: module.AuditPlanListPage,
    }),
  ),
);
const AuditPlanCreatePage = lazy(() =>
  import(
    "@/modules/internal-audit/presentation/pages/AuditPlanCreatePage"
  ).then((module) => ({
    default: module.AuditPlanCreatePage,
  })),
);
const AuditPlanDetailPage = lazy(() =>
  import(
    "@/modules/internal-audit/presentation/pages/AuditPlanDetailPage"
  ).then((module) => ({
    default: module.AuditPlanDetailPage,
  })),
);
const AuditPlanPdfPreviewPage = lazy(() =>
  import(
    "@/modules/internal-audit/presentation/pages/AuditPlanPdfPreviewPage"
  ).then((module) => ({
    default: module.AuditPlanPdfPreviewPage,
  })),
);
const AuditRecordListPage = lazy(() =>
  import(
    "@/modules/internal-audit/presentation/pages/AuditRecordListPage"
  ).then((module) => ({
    default: module.AuditRecordListPage,
  })),
);
const AuditRecordDetailPage = lazy(() =>
  import(
    "@/modules/internal-audit/presentation/pages/AuditRecordDetailPage"
  ).then((module) => ({
    default: module.AuditRecordDetailPage,
  })),
);
const AuditIssueListPage = lazy(() =>
  import("@/modules/internal-audit/presentation/pages/AuditIssueListPage").then(
    (module) => ({
      default: module.AuditIssueListPage,
    }),
  ),
);
const AuditSummaryPage = lazy(() =>
  import("@/modules/internal-audit/presentation/pages/AuditSummaryPage").then(
    (module) => ({
      default: module.AuditSummaryPage,
    }),
  ),
);
const AuditRecordEditPage = lazy(() =>
  import(
    "@/modules/internal-audit/presentation/pages/AuditRecordEditPage"
  ).then((module) => ({
    default: module.AuditRecordEditPage,
  })),
);
const FormsListPage = lazy(() =>
  import("@/modules/form-center/presentation/pages/FormsListPage").then(
    (module) => ({
      default: module.FormsListPage,
    }),
  ),
);
const CategoriesPage = lazy(() =>
  import("@/modules/form-center/presentation/pages/CategoriesPage").then(
    (module) => ({
      default: module.CategoriesPage,
    }),
  ),
);
const CategoryFormPage = lazy(() =>
  import("@/modules/form-center/presentation/pages/CategoryFormPage").then(
    (module) => ({
      default: module.CategoryFormPage,
    }),
  ),
);
const FormBuilderPage = lazy(() =>
  import("@/modules/form-center/presentation/pages/FormBuilderPage").then(
    (module) => ({
      default: module.FormBuilderPage,
    }),
  ),
);
const FormDetailPage = lazy(() =>
  import("@/modules/form-center/presentation/pages/FormDetailPage").then(
    (module) => ({
      default: module.FormDetailPage,
    }),
  ),
);
const FormAnalysisPage = lazy(() =>
  import("@/modules/form-center/presentation/pages/FormAnalysisPage").then(
    (module) => ({
      default: module.FormAnalysisPage,
    }),
  ),
);
const FormPreviewPage = lazy(() =>
  import("@/modules/form-center/presentation/pages/FormPreviewPage").then(
    (module) => ({
      default: module.FormPreviewPage,
    }),
  ),
);
const FormSubmissionsPage = lazy(() =>
  import("@/modules/form-center/presentation/pages/FormSubmissionsPage").then(
    (module) => ({
      default: module.FormSubmissionsPage,
    }),
  ),
);
const FormSubmissionDetailPage = lazy(() =>
  import(
    "@/modules/form-center/presentation/pages/FormSubmissionDetailPage"
  ).then((module) => ({
    default: module.FormSubmissionDetailPage,
  })),
);
const AuditRecordPdfPreviewPage = lazy(() =>
  import(
    "@/modules/internal-audit/presentation/pages/AuditRecordPdfPreviewPage"
  ).then((module) => ({
    default: module.AuditRecordPdfPreviewPage,
  })),
);
const AuditRecordNewPage = lazy(() =>
  import("@/modules/internal-audit/presentation/pages/AuditRecordNewPage").then(
    (module) => ({
      default: module.AuditRecordNewPage,
    }),
  ),
);
const AuditRecordCreatePage = lazy(() =>
  import(
    "@/modules/internal-audit/presentation/pages/AuditRecordCreatePage"
  ).then((module) => ({
    default: module.AuditRecordCreatePage,
  })),
);
const rootRoute = createRootRoute({
  component: RootLayout,
  errorComponent: ErrorBoundary,
  beforeLoad: ({ location }) => {
    if (
      location.pathname === "/" ||
      location.pathname === "/app" ||
      location.pathname === "/app/"
    ) {
      throw redirect({ to: "/app/dashboard", replace: true });
    }
  },
});

function parseSortParam(
  v: unknown,
): Array<{ field: string; dir: "asc" | "desc" }> | undefined {
  if (Array.isArray(v) && v.length > 0)
    return v as Array<{ field: string; dir: "asc" | "desc" }>;
  if (typeof v === "string") {
    try {
      const parsed = JSON.parse(v) as unknown;
      return Array.isArray(parsed) && parsed.length > 0
        ? (parsed as Array<{ field: string; dir: "asc" | "desc" }>)
        : undefined;
    } catch {
      return undefined;
    }
  }
  return undefined;
}
const authLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auth",
  component: AuthLayout,
});

const loginRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/login",
  component: LoginPage,
});

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/app",
  component: AuthenticatedLayout,
});

const dashboardRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/dashboard",
  component: () => (
    <LazyPage>
      <DashboardPage />
    </LazyPage>
  ),
});

const rolesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/roles",
  component: () => (
    <RequirePermissions all={["users:read"]} any={["users:ban"]}>
      <LazyPage>
        <RolesPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const roleCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/roles/create",
  component: () => (
    <RequirePermissions all={["users:read"]} any={["users:ban"]}>
      <LazyPage>
        <RoleCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const roleEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/roles/$id/edit",
  component: () => (
    <RequirePermissions all={["users:read"]} any={["users:ban"]}>
      <LazyPage>
        <RoleEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

// const auditRoute = createRoute({
//   getParentRoute: () => appRoute,
//   path: "/audit",
//   component: () => (
//     <RequirePermissions all={["audit:read"]}>
//       <LazyPage>
//         <AuditPage />
//       </LazyPage>
//     </RequirePermissions>
//   ),
// });

// const auditDetailRoute = createRoute({
//   getParentRoute: () => appRoute,
//   path: "/audit/$id",
//   component: () => (
//     <RequirePermissions all={["audit:read"]}>
//       <LazyPage>
//         <AuditDetailPage />
//       </LazyPage>
//     </RequirePermissions>
//   ),
// });

const usersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/users",
  component: () => (
    <RequirePermissions all={["users:read"]}>
      <LazyPage>
        <UsersPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const userProfileRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/users/$userId/profile",
  component: () => (
    <RequirePermissions all={["users:read"]}>
      <LazyPage>
        <UserProfilePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const positionsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/positions",
  component: () => (
    <RequirePermissions all={[AppPermissions.positions.read]}>
      <LazyPage>
        <PositionsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const departmentsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/departments",
  component: () => (
    <RequirePermissions all={[AppPermissions.departments.read]}>
      <LazyPage>
        <DepartmentsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const sopRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/sop",
  component: () => (
    <RequirePermissions all={[AppPermissions.sop.read]}>
      <LazyPage>
        <SOPPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const sopCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/sop/new",
  component: () => (
    <RequirePermissions all={[AppPermissions.sop.create]}>
      <LazyPage>
        <SOPCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const sopEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/sop/$id/edit",
  component: () => (
    <RequirePermissions all={[AppPermissions.sop.update]}>
      <LazyPage>
        <SOPEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const sopDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/sop/$id",
  component: () => (
    <RequirePermissions all={[AppPermissions.sop.read]}>
      <LazyPage>
        <SOPDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const sopPdfPreviewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/sop/$id/pdf-preview",
  component: () => (
    <RequirePermissions all={[AppPermissions.sop.read]}>
      <LazyPage>
        <SOPPdfPreviewPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const sopMappingRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/sop-mapping",
  component: () => (
    <RequirePermissions all={[AppPermissions.sop.read]}>
      <LazyPage>
        <SOPMappingPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const orgChartRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/org-chart",
  component: () => (
    <RequirePermissions all={[AppPermissions["org-chart"].read]}>
      <LazyPage>
        <OrgChartOverviewPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const assignPositionRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/assign-position",
  component: () => (
    <RequirePermissions all={[AppPermissions.assignments.read]}>
      <LazyPage>
        <AssignPositionPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const profileRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/profile",
  component: () => (
    <LazyPage>
      <ProfilePage />
    </LazyPage>
  ),
});

const jdRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/jd",
  component: () => (
    <RequirePermissions all={["jd:read"]}>
      <LazyPage>
        <JDListPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const jdDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/jd/$id",
  component: () => (
    <RequirePermissions all={["jd:read"]}>
      <LazyPage>
        <JDDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const jdPdfPreviewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/jd/$id/pdf-preview",
  component: () => (
    <RequirePermissions all={["jd:read"]}>
      <LazyPage>
        <JDPdfPreviewPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const jdCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/jd/new",
  component: () => (
    <RequirePermissions all={["jd:create"]}>
      <LazyPage>
        <JDCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const jdEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/jd/$id/edit",
  component: () => (
    <RequirePermissions all={["jd:update"]}>
      <LazyPage>
        <JDCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const jdMappingRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/jd-mapping",
  component: () => (
    <RequirePermissions all={["jd_mapping:read"]}>
      <LazyPage>
        <JDMappingPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const forbiddenRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/errors/forbidden",
  component: () => (
    <LazyPage>
      <Forbidden />
    </LazyPage>
  ),
});

const checklistsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/checklists",
  component: () => (
    <RequirePermissions all={[AppPermissions.checklists.read]}>
      <LazyPage>
        <ChecklistsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const checklistExecutionHistoryRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/checklists/execution-history",
  validateSearch: (search: Record<string, unknown>) => ({
    offset: Number(search.offset ?? 0) || 0,
    limit: Number(search.limit ?? 20) || 20,
    checklistId:
      typeof search.checklistId === "string" && search.checklistId.length > 0
        ? search.checklistId
        : undefined,
    departmentId:
      typeof search.departmentId === "string" && search.departmentId.length > 0
        ? search.departmentId
        : undefined,
    userId:
      typeof search.userId === "string" && search.userId.length > 0
        ? search.userId
        : undefined,
  }),
  component: () => (
    <RequirePermissions all={[AppPermissions.checklists.read]}>
      <LazyPage>
        <ChecklistExecutionHistoryPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const checklistResultRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/checklists/result",
  validateSearch: (search: Record<string, unknown>) => ({
    departmentId:
      typeof search.departmentId === "string" && search.departmentId.length > 0
        ? search.departmentId
        : undefined,
    checklistId:
      typeof search.checklistId === "string" && search.checklistId.length > 0
        ? search.checklistId
        : undefined,
    startDate:
      typeof search.startDate === "string" && search.startDate.length > 0
        ? search.startDate
        : undefined,
    endDate:
      typeof search.endDate === "string" && search.endDate.length > 0
        ? search.endDate
        : undefined,
  }),
  component: () => (
    <RequirePermissions all={[AppPermissions.checklists.read]}>
      <LazyPage>
        <ChecklistResultPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const checklistExecutionDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/checklists/execution-history/$executionId",
  component: () => (
    <RequirePermissions all={[AppPermissions.checklists.read]}>
      <LazyPage>
        <ChecklistExecutionDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const checklistDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/checklists/$id",
  component: () => (
    <RequirePermissions all={[AppPermissions.checklists.read]}>
      <LazyPage>
        <ChecklistDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const checklistCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/checklists/new",
  component: () => (
    <RequirePermissions all={[AppPermissions.checklists.create]}>
      <LazyPage>
        <ChecklistCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const checklistEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/checklists/$id/edit",
  component: () => (
    <RequirePermissions all={[AppPermissions.checklists.update]}>
      <LazyPage>
        <ChecklistEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const flowsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/flows",
  component: () => (
    <RequirePermissions all={[AppPermissions.flows.read]}>
      <LazyPage>
        <FlowsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const flowDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/flows/$id",
  component: () => (
    <RequirePermissions all={[AppPermissions.flows.read]}>
      <LazyPage>
        <FlowDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const flowEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/flows/$id/edit",
  component: () => (
    <RequirePermissions all={[AppPermissions.flows.update]}>
      <LazyPage>
        <FlowEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const qcRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/qc",
  component: () => (
    <RequirePermissions all={[AppPermissions.qc.read]}>
      <LazyPage>
        <QCListPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const qcDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/qc/$id",
  component: () => (
    <RequirePermissions all={[AppPermissions.qc.read]}>
      <LazyPage>
        <QCDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const qcPdfPreviewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/qc/$id/pdf-preview",
  component: () => (
    <RequirePermissions all={[AppPermissions.qc.read]}>
      <LazyPage>
        <QCPdfPreviewPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const qcCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/qc/new",
  component: () => (
    <RequirePermissions all={[AppPermissions.qc.create]}>
      <LazyPage>
        <QCCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const qcEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/qc/$id/edit",
  component: () => (
    <RequirePermissions all={[AppPermissions.qc.update]}>
      <LazyPage>
        <QCEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const qcRecordListRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/qc/records",
  validateSearch: (search: Record<string, unknown>) => {
    return {
      offset: (search.offset as number | undefined) ?? 0,
      limit: (search.limit as number | undefined) ?? 20,
      sort: search.sort as
        | Array<{ field: string; dir: "asc" | "desc" }>
        | undefined,
      filters: search.filters as
        | Array<{ field: string; op: string; value: unknown }>
        | undefined,
    };
  },
  component: () => (
    <RequirePermissions all={[AppPermissions.qc_record.read]}>
      <LazyPage>
        <QCRecordListPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const qcRecordRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/qc/$id/record",
  component: () => (
    <RequirePermissions all={[AppPermissions.qc_record.create]}>
      <LazyPage>
        <QCRecordPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const qcRecordEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/qc/$id/record/$recordId",
  component: () => (
    <RequirePermissions all={[AppPermissions.qc_record.update]}>
      <LazyPage>
        <QCRecordPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const qcRecordDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/qc/records/$recordId",
  component: () => (
    <RequirePermissions all={[AppPermissions.qc_record.read]}>
      <LazyPage>
        <QCRecordDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const qcRecordPdfPreviewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/qc/records/$recordId/pdf-preview",
  component: () => (
    <RequirePermissions all={[AppPermissions.qc_record.read]}>
      <LazyPage>
        <QCRecordPdfPreviewPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const qcResultRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/qc/result",
  component: () => (
    <RequirePermissions all={[AppPermissions.qc_result.read]}>
      <LazyPage>
        <QCResultPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const kpiRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/kpi",
  beforeLoad: () => {
    throw redirect({ to: "/app/management/okr-kpi", replace: true });
  },
  component: () => null,
});
const kpiDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/kpi/$id",
  component: () => (
    <RequirePermissions all={[AppPermissions.kpi.read]}>
      <LazyPage>
        <KPIDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const kpisRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/kpis",
  component: () => (
    <RequirePermissions all={[AppPermissions.kpi.read]}>
      <LazyPage>
        <KPIsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const kpiCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/kpi/create",
  component: () => (
    <RequirePermissions all={[AppPermissions.kpi.create]}>
      <LazyPage>
        <KPICreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const kpiEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/kpi/$id/edit",
  component: () => (
    <RequirePermissions all={[AppPermissions.kpi.update]}>
      <LazyPage>
        <KPIEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const kpiPdfPreviewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/kpi/$id/pdf-preview",
  component: () => (
    <RequirePermissions all={[AppPermissions.kpi.read]}>
      <LazyPage>
        <KPIPdfPreviewPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const kpiCalculationRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/kpi/calculation",
  beforeLoad: () => {
    throw redirect({
      to: "/app/management/okr-kpi/calculation",
      replace: true,
    });
  },
  component: () => null,
});
const serviceStandardRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/service-standard",
  component: () => (
    <RequirePermissions all={[AppPermissions.service_standard.read]}>
      <LazyPage>
        <ServiceStandardListPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const serviceStandardDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/service-standard/$id",
  component: () => (
    <RequirePermissions all={[AppPermissions.service_standard.read]}>
      <LazyPage>
        <ServiceStandardDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const serviceStandardCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/service-standard/new",
  component: () => (
    <RequirePermissions all={[AppPermissions.service_standard.create]}>
      <LazyPage>
        <ServiceStandardCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const serviceStandardEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/service-standard/$id/edit",
  component: () => (
    <RequirePermissions all={[AppPermissions.service_standard.update]}>
      <LazyPage>
        <ServiceStandardEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const serviceStandardPdfPreviewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/service-standard/$id/pdf-preview",
  component: () => (
    <RequirePermissions all={[AppPermissions.service_standard.read]}>
      <LazyPage>
        <ServiceStandardPdfPreviewPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const templateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/template",
  component: () => (
    <RequirePermissions all={[AppPermissions.template.read]}>
      <LazyPage>
        <TemplateListPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const templateDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/template/$id",
  component: () => (
    <RequirePermissions all={[AppPermissions.template.read]}>
      <LazyPage>
        <TemplateDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const templateCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/template/new",
  component: () => (
    <RequirePermissions all={[AppPermissions.template.create]}>
      <LazyPage>
        <TemplateCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const templateEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/template/$id/edit",
  component: () => (
    <RequirePermissions all={[AppPermissions.template.update]}>
      <LazyPage>
        <TemplateCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const templateByDepartmentRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/template/by-department",
  component: () => (
    <RequirePermissions all={[AppPermissions.template.read]}>
      <LazyPage>
        <TemplateByDepartmentPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const serviceStandardMappingRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/service-standard-mapping",
  validateSearch: (search: Record<string, unknown>) => {
    return {
      offset: (search.offset as number | undefined) ?? 0,
      limit: (search.limit as number | undefined) ?? 20,
      sort: search.sort as
        | Array<{ field: string; dir: "asc" | "desc" }>
        | undefined,
      filters: search.filters as
        | Array<{ field: string; op: string; value: unknown }>
        | undefined,
      serviceStandardId: search.serviceStandardId as string | undefined,
      positionId: search.positionId as string | undefined,
      checklistId: search.checklistId as string | undefined,
      qcStandardId: search.qcStandardId as string | undefined,
      kpiId: search.kpiId as string | undefined,
      search: search.search as string | undefined,
    };
  },
  component: () => (
    <RequirePermissions all={[AppPermissions.service_standard.read]}>
      <LazyPage>
        <ServiceStandardMappingPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const internalAuditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/internal-audit",
  validateSearch: (search: Record<string, unknown>) => {
    return {
      offset: (search.offset as number | undefined) ?? 0,
      limit: (search.limit as number | undefined) ?? 20,
      sort: search.sort as
        | Array<{ field: string; dir: "asc" | "desc" }>
        | undefined,
      filters: search.filters as
        | Array<{ field: string; op: string; value: unknown }>
        | undefined,
    };
  },
  component: () => (
    <RequirePermissions all={[AppPermissions.internal_audit.read]}>
      <LazyPage>
        <AuditPlanListPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const internalAuditPdfPreviewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/internal-audit/$id/pdf-preview",
  component: () => (
    <RequirePermissions all={[AppPermissions.internal_audit.read]}>
      <LazyPage>
        <AuditPlanPdfPreviewPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const internalAuditDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/internal-audit/$id",
  component: () => (
    <RequirePermissions all={[AppPermissions.internal_audit.read]}>
      <LazyPage>
        <AuditPlanDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const internalAuditCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/internal-audit/new",
  component: () => (
    <RequirePermissions all={[AppPermissions.internal_audit.create]}>
      <LazyPage>
        <AuditPlanCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const internalAuditEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/internal-audit/$id/edit",
  component: () => (
    <RequirePermissions all={[AppPermissions.internal_audit.update]}>
      <LazyPage>
        <AuditPlanCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const internalAuditRecordListRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/internal-audit/records",
  validateSearch: (search: Record<string, unknown>) => {
    return {
      offset: (search.offset as number | undefined) ?? 0,
      limit: (search.limit as number | undefined) ?? 20,
      sort: search.sort as
        | Array<{ field: string; dir: "asc" | "desc" }>
        | undefined,
      filters: search.filters as
        | Array<{ field: string; op: string; value: unknown }>
        | undefined,
    };
  },
  component: () => (
    <RequirePermissions all={[AppPermissions.internal_audit.read]}>
      <LazyPage>
        <AuditRecordListPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const internalAuditRecordNewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/internal-audit/records/new",
  validateSearch: (search: Record<string, unknown>) => {
    return {
      offset: (search.offset as number | undefined) ?? 0,
      limit: (search.limit as number | undefined) ?? 20,
      sort: search.sort as
        | Array<{ field: string; dir: "asc" | "desc" }>
        | undefined,
      filters: search.filters as
        | Array<{ field: string; op: string; value: unknown }>
        | undefined,
      planId: search.planId as string | undefined,
    };
  },
  component: () => (
    <RequirePermissions all={[AppPermissions.internal_audit.create]}>
      <LazyPage>
        <AuditRecordNewPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const internalAuditRecordCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/internal-audit/records/new/$planId",
  component: () => (
    <RequirePermissions all={[AppPermissions.internal_audit.create]}>
      <LazyPage>
        <AuditRecordCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const internalAuditRecordEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/internal-audit/records/$id/edit",
  component: () => (
    <RequirePermissions all={[AppPermissions.internal_audit.update]}>
      <LazyPage>
        <AuditRecordEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const internalAuditRecordPdfPreviewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/internal-audit/records/$id/pdf-preview",
  component: () => (
    <RequirePermissions all={[AppPermissions.internal_audit.read]}>
      <LazyPage>
        <AuditRecordPdfPreviewPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const internalAuditRecordDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/internal-audit/records/$id",
  component: () => (
    <RequirePermissions all={[AppPermissions.internal_audit.read]}>
      <LazyPage>
        <AuditRecordDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const internalAuditIssueListRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/internal-audit/issues",
  validateSearch: (search: Record<string, unknown>) => {
    return {
      offset: (search.offset as number | undefined) ?? 0,
      limit: (search.limit as number | undefined) ?? 20,
      sort: search.sort as
        | Array<{ field: string; dir: "asc" | "desc" }>
        | undefined,
      filters: search.filters as
        | Array<{ field: string; op: string; value: unknown }>
        | undefined,
    };
  },
  component: () => (
    <RequirePermissions all={[AppPermissions.internal_audit.read]}>
      <LazyPage>
        <AuditIssueListPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const internalAuditSummaryRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/internal-audit/summary",
  component: () => (
    <RequirePermissions all={[AppPermissions.internal_audit.summary]}>
      <LazyPage>
        <AuditSummaryPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const formsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/forms",
  validateSearch: (search: Record<string, unknown>) => {
    return {
      offset: (search.offset as number | undefined) ?? 0,
      limit: (search.limit as number | undefined) ?? 20,
      sort: search.sort as
        | Array<{ field: string; dir: "asc" | "desc" }>
        | undefined,
      filters: search.filters as
        | Array<{ field: string; op: string; value: unknown }>
        | undefined,
    };
  },
  component: () => (
    <RequirePermissions all={[AppPermissions.forms.read]}>
      <LazyPage>
        <FormsListPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const ContractsPage = lazy(() =>
  import("@/modules/contracts/presentation/pages/ContractsPage").then(
    (module) => ({
      default: module.ContractsPage,
    }),
  ),
);
const ContractDetailPage = lazy(() =>
  import("@/modules/contracts/presentation/pages/ContractDetailPage").then(
    (module) => ({
      default: module.ContractDetailPage,
    }),
  ),
);
const ContractPdfPreviewPage = lazy(() =>
  import("@/modules/contracts/presentation/pages/ContractPdfPreviewPage").then(
    (module) => ({
      default: module.ContractPdfPreviewPage,
    }),
  ),
);
const ContractExpiryRenewalPage = lazy(() =>
  import(
    "@/modules/contracts/presentation/pages/ContractExpiryRenewalPage"
  ).then((module) => ({
    default: module.ContractExpiryRenewalPage,
  })),
);
const ContractCreatePage = lazy(() =>
  import("@/modules/contracts/presentation/pages/ContractCreatePage").then(
    (module) => ({
      default: module.ContractCreatePage,
    }),
  ),
);
const ContractEditPage = lazy(() =>
  import("@/modules/contracts/presentation/pages/ContractEditPage").then(
    (module) => ({
      default: module.ContractEditPage,
    }),
  ),
);
const contractsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/contracts",
  component: () => (
    <RequirePermissions all={[AppPermissions.contracts.read]}>
      <LazyPage>
        <ContractsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const formCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/forms/new",
  component: () => (
    <RequirePermissions all={[AppPermissions.forms.create]}>
      <LazyPage>
        <FormBuilderPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const contractCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/contracts/new",
  component: () => (
    <RequirePermissions all={[AppPermissions.contracts.create]}>
      <LazyPage>
        <ContractCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const formDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/forms/$id",
  component: () => (
    <RequirePermissions all={[AppPermissions.forms.read]}>
      <LazyPage>
        <FormDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const contractEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/contracts/$id/edit",
  component: () => (
    <RequirePermissions all={[AppPermissions.contracts.update]}>
      <LazyPage>
        <ContractEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const formEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/forms/$id/edit",
  component: () => (
    <RequirePermissions all={[AppPermissions.forms.update]}>
      <LazyPage>
        <FormBuilderPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const contractPdfPreviewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/contracts/$id/pdf-preview",
  component: () => (
    <RequirePermissions all={[AppPermissions.contracts.read]}>
      <LazyPage>
        <ContractPdfPreviewPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const formAnalysisRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/forms/$id/analysis",
  component: () => (
    <RequirePermissions all={[AppPermissions.forms.read]}>
      <LazyPage>
        <FormAnalysisPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const contractDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/contracts/$id",
  component: () => (
    <RequirePermissions all={[AppPermissions.contracts.read]}>
      <LazyPage>
        <ContractDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const formPreviewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/forms/$id/preview",
  component: () => (
    <RequirePermissions all={[AppPermissions.forms.read]}>
      <LazyPage>
        <FormPreviewPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const formSubmissionsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/forms/$id/submissions",
  validateSearch: (search: Record<string, unknown>) => ({
    offset: (search.offset as number | undefined) ?? 0,
    limit: (search.limit as number | undefined) ?? 20,
    sort: search.sort as
      | Array<{ field: string; dir: "asc" | "desc" }>
      | undefined,
    filters: search.filters as
      | Array<{ field: string; op: string; value: unknown }>
      | undefined,
  }),
  component: () => (
    <RequirePermissions all={[AppPermissions.forms.read]}>
      <LazyPage>
        <FormSubmissionsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const formSubmissionDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/forms/$id/submissions/$submissionId",
  component: () => (
    <RequirePermissions all={[AppPermissions.forms.read]}>
      <LazyPage>
        <FormSubmissionDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const categoriesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/forms/categories",
  validateSearch: (search: Record<string, unknown>) => {
    return {
      offset: (search.offset as number | undefined) ?? 0,
      limit: (search.limit as number | undefined) ?? 20,
      sort: search.sort as
        | Array<{ field: string; dir: "asc" | "desc" }>
        | undefined,
      filters: search.filters as
        | Array<{ field: string; op: string; value: unknown }>
        | undefined,
    };
  },
  component: () => (
    <RequirePermissions all={[AppPermissions.forms.read]}>
      <LazyPage>
        <CategoriesPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const categoryCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/forms/categories/new",
  component: () => (
    <RequirePermissions all={[AppPermissions.forms.create]}>
      <LazyPage>
        <CategoryFormPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const categoryEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/forms/categories/$id/edit",
  component: () => (
    <RequirePermissions all={[AppPermissions.forms.update]}>
      <LazyPage>
        <CategoryFormPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const contractExpiryRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/contracts/expiring",
  component: () => (
    <RequirePermissions all={[AppPermissions.contracts.read]}>
      <LazyPage>
        <ContractExpiryRenewalPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const QuotationInvoiceDocumentsPage = lazy(() =>
  import(
    "@/modules/quotation-invoice/presentation/pages/QuotationInvoiceDocumentsPage"
  ).then((module) => ({
    default: module.QuotationInvoiceDocumentsPage,
  })),
);
const QuotationInvoiceDocumentDetailPage = lazy(() =>
  import(
    "@/modules/quotation-invoice/presentation/pages/QuotationInvoiceDocumentDetailPage"
  ).then((module) => ({
    default: module.QuotationInvoiceDocumentDetailPage,
  })),
);
const QuotationInvoiceDocumentCreatePage = lazy(() =>
  import(
    "@/modules/quotation-invoice/presentation/pages/QuotationInvoiceDocumentCreatePage"
  ).then((module) => ({
    default: module.QuotationInvoiceDocumentCreatePage,
  })),
);
const QuotationInvoiceDocumentEditPage = lazy(() =>
  import(
    "@/modules/quotation-invoice/presentation/pages/QuotationInvoiceDocumentEditPage"
  ).then((module) => ({
    default: module.QuotationInvoiceDocumentEditPage,
  })),
);
const QuotationInvoiceDocumentPdfPreviewPage = lazy(() =>
  import(
    "@/modules/quotation-invoice/presentation/pages/QuotationInvoiceDocumentPdfPreviewPage"
  ).then((module) => ({
    default: module.QuotationInvoiceDocumentPdfPreviewPage,
  })),
);
const QuotationInvoiceDocumentAuditPage = lazy(() =>
  import(
    "@/modules/quotation-invoice/presentation/pages/QuotationInvoiceDocumentAuditPage"
  ).then((module) => ({
    default: module.QuotationInvoiceDocumentAuditPage,
  })),
);
const QuotationInvoiceIssuerSettingsPage = lazy(() =>
  import(
    "@/modules/quotation-invoice/presentation/pages/QuotationInvoiceIssuerSettingsPage"
  ).then((module) => ({
    default: module.QuotationInvoiceIssuerSettingsPage,
  })),
);
const documentsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/documents",
  validateSearch: (search: Record<string, unknown>) => ({
    offset: (search.offset as number | undefined) ?? 0,
    limit: (search.limit as number | undefined) ?? 20,
    sort: search.sort as
      | Array<{ field: string; dir: "asc" | "desc" }>
      | undefined,
    filters: search.filters as
      | Array<{ field: string; op: string; value: unknown }>
      | undefined,
  }),
  component: () => (
    <RequirePermissions all={[AppPermissions.quotation_invoice.read]}>
      <LazyPage>
        <QuotationInvoiceDocumentsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const documentsSettingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/documents/settings",
  component: () => (
    <RequirePermissions all={[AppPermissions.quotation_invoice.read]}>
      <LazyPage>
        <QuotationInvoiceIssuerSettingsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const documentsDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/documents/$id",
  component: () => (
    <RequirePermissions all={[AppPermissions.quotation_invoice.read]}>
      <LazyPage>
        <QuotationInvoiceDocumentDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const documentsPdfPreviewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/documents/$id/pdf-preview",
  validateSearch: (search: Record<string, unknown>) => ({
    print: (search.print as number | undefined) ?? undefined,
  }),
  component: () => (
    <RequirePermissions all={[AppPermissions.quotation_invoice.read]}>
      <LazyPage>
        <QuotationInvoiceDocumentPdfPreviewPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const documentsNewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/documents/new",
  validateSearch: qiCreatePrefillValidateSearch,
  component: () => (
    <RequirePermissions all={[AppPermissions.quotation_invoice.create]}>
      <LazyPage>
        <QuotationInvoiceDocumentCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const documentsEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/documents/$id/edit",
  component: () => (
    <RequirePermissions all={[AppPermissions.quotation_invoice.update]}>
      <LazyPage>
        <QuotationInvoiceDocumentEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const documentsAuditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/documents/audit",
  component: () => (
    <RequirePermissions all={[AppPermissions.quotation_invoice.read]}>
      <LazyPage>
        <QuotationInvoiceDocumentAuditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const PayrollListPage = lazy(() =>
  import("@/modules/hr/payroll/presentation/pages/PayrollListPage").then(
    (module) => ({
      default: module.PayrollListPage,
    }),
  ),
);
const PayrollByPeriodPage = lazy(() =>
  import("@/modules/hr/payroll/presentation/pages/PayrollByPeriodPage").then(
    (module) => ({
      default: module.PayrollByPeriodPage,
    }),
  ),
);
const PayrollNewPage = lazy(() =>
  import("@/modules/hr/payroll/presentation/pages/PayrollNewPage").then(
    (module) => ({
      default: module.PayrollNewPage,
    }),
  ),
);
const PayrollFormPage = lazy(() =>
  import("@/modules/hr/payroll/presentation/pages/PayrollFormPage").then(
    (module) => ({
      default: module.PayrollFormPage,
    }),
  ),
);
const PayslipViewPage = lazy(() =>
  import("@/modules/hr/payroll/presentation/pages/PayslipViewPage").then(
    (module) => ({
      default: module.PayslipViewPage,
    }),
  ),
);
const PayrollHistoryPage = lazy(() =>
  import("@/modules/hr/payroll/presentation/pages/PayrollHistoryPage").then(
    (module) => ({
      default: module.PayrollHistoryPage,
    }),
  ),
);
const OTRulesPage = lazy(() =>
  import("@/modules/hr/overtime/presentation/pages/OTRulesPage").then(
    (module) => ({
      default: module.OTRulesPage,
    }),
  ),
);
const OTRequestListPage = lazy(() =>
  import("@/modules/hr/overtime/presentation/pages/OTRequestListPage").then(
    (module) => ({
      default: module.OTRequestListPage,
    }),
  ),
);
const OTApprovalListPage = lazy(() =>
  import("@/modules/hr/overtime/presentation/pages/OTApprovalListPage").then(
    (module) => ({
      default: module.OTApprovalListPage,
    }),
  ),
);
const OTSummaryPage = lazy(() =>
  import("@/modules/hr/overtime/presentation/pages/OTSummaryPage").then(
    (module) => ({
      default: module.OTSummaryPage,
    }),
  ),
);
const OTPlaceholderPage = lazy(() =>
  import("@/modules/hr/overtime/presentation/pages/OTPlaceholderPage").then(
    (module) => ({
      default: module.OTPlaceholderPage,
    }),
  ),
);
const OTRequestFormPage = lazy(() =>
  import("@/modules/hr/overtime/presentation/pages/OTRequestFormPage").then(
    (module) => ({
      default: module.OTRequestFormPage,
    }),
  ),
);
const OTRequestFormForEmployeePage = lazy(() =>
  import(
    "@/modules/hr/overtime/presentation/pages/OTRequestFormForEmployeePage"
  ).then((module) => ({
    default: module.OTRequestFormForEmployeePage,
  })),
);
const OTRequestDetailPage = lazy(() =>
  import("@/modules/hr/overtime/presentation/pages/OTRequestDetailPage").then(
    (module) => ({
      default: module.OTRequestDetailPage,
    }),
  ),
);
const OTRuleCreatePage = lazy(() =>
  import("@/modules/hr/overtime/presentation/pages/OTRuleCreatePage").then(
    (module) => ({
      default: module.OTRuleCreatePage,
    }),
  ),
);
const OTRuleEditPage = lazy(() =>
  import("@/modules/hr/overtime/presentation/pages/OTRuleEditPage").then(
    (module) => ({
      default: module.OTRuleEditPage,
    }),
  ),
);
const OTRuleDetailPage = lazy(() =>
  import("@/modules/hr/overtime/presentation/pages/OTRuleDetailPage").then(
    (module) => ({
      default: module.OTRuleDetailPage,
    }),
  ),
);
const LeaveTypesPage = lazy(() =>
  import("@/modules/hr/leave/presentation/pages/LeaveTypesPage").then(
    (module) => ({
      default: module.LeaveTypesPage,
    }),
  ),
);
const LeaveTypeCreatePage = lazy(() =>
  import("@/modules/hr/leave/presentation/pages/LeaveTypeCreatePage").then(
    (module) => ({
      default: module.LeaveTypeCreatePage,
    }),
  ),
);
const LeaveTypeDetailPage = lazy(() =>
  import("@/modules/hr/leave/presentation/pages/LeaveTypeDetailPage").then(
    (module) => ({
      default: module.LeaveTypeDetailPage,
    }),
  ),
);
const LeaveTypeEditPage = lazy(() =>
  import("@/modules/hr/leave/presentation/pages/LeaveTypeEditPage").then(
    (module) => ({
      default: module.LeaveTypeEditPage,
    }),
  ),
);
const LeaveEntitlementsPage = lazy(() =>
  import("@/modules/hr/leave/presentation/pages/LeaveEntitlementsPage").then(
    (module) => ({
      default: module.LeaveEntitlementsPage,
    }),
  ),
);
const LeaveEntitlementByEmployeePage = lazy(() =>
  import(
    "@/modules/hr/leave/presentation/pages/LeaveEntitlementByEmployeePage"
  ).then((module) => ({
    default: module.LeaveEntitlementByEmployeePage,
  })),
);
const LeaveEntitlementCreatePage = lazy(() =>
  import(
    "@/modules/hr/leave/presentation/pages/LeaveEntitlementCreatePage"
  ).then((module) => ({
    default: module.LeaveEntitlementCreatePage,
  })),
);
const LeaveApprovalsPage = lazy(() =>
  import("@/modules/hr/leave/presentation/pages/LeaveApprovalsPage").then(
    (module) => ({
      default: module.LeaveApprovalsPage,
    }),
  ),
);
const LeaveHistoryPage = lazy(() =>
  import("@/modules/hr/leave/presentation/pages/LeaveHistoryPage").then(
    (module) => ({
      default: module.LeaveHistoryPage,
    }),
  ),
);
const LeaveRequestCreatePage = lazy(() =>
  import("@/modules/hr/leave/presentation/pages/LeaveRequestCreatePage").then(
    (module) => ({
      default: module.LeaveRequestCreatePage,
    }),
  ),
);
const LeaveRequestEditPage = lazy(() =>
  import("@/modules/hr/leave/presentation/pages/LeaveRequestEditPage").then(
    (module) => ({
      default: module.LeaveRequestEditPage,
    }),
  ),
);
const LeaveRequestDetailPage = lazy(() =>
  import("@/modules/hr/leave/presentation/pages/LeaveRequestDetailPage").then(
    (module) => ({
      default: module.LeaveRequestDetailPage,
    }),
  ),
);
const PolicyListPage = lazy(() =>
  import("@/modules/hr/company-policy/presentation/pages/PolicyListPage").then(
    (module) => ({
      default: module.PolicyListPage,
    }),
  ),
);
const ProjectListPage = lazy(() =>
  import(
    "@/modules/management/project/presentation/pages/ProjectListPage"
  ).then((module) => ({
    default: module.ProjectListPage,
  })),
);
const ProjectSummaryPage = lazy(() =>
  import(
    "@/modules/management/project/presentation/pages/ProjectSummaryPage"
  ).then((module) => ({
    default: module.ProjectSummaryPage,
  })),
);
const ProjectDetailPage = lazy(() =>
  import(
    "@/modules/management/project/presentation/pages/ProjectDetailPage"
  ).then((module) => ({
    default: module.ProjectDetailPage,
  })),
);
const ProjectFormPage = lazy(() =>
  import(
    "@/modules/management/project/presentation/pages/ProjectFormPage"
  ).then((module) => ({
    default: module.ProjectFormPage,
  })),
);
const MonthlyReportListPage = lazy(() =>
  import(
    "@/modules/management/monthly-report/presentation/pages/MonthlyReportListPage"
  ).then((module) => ({
    default: module.MonthlyReportListPage,
  })),
);
const MonthlyReportDetailPage = lazy(() =>
  import(
    "@/modules/management/monthly-report/presentation/pages/MonthlyReportDetailPage"
  ).then((module) => ({
    default: module.MonthlyReportDetailPage,
  })),
);
const MonthlyReportFormPage = lazy(() =>
  import(
    "@/modules/management/monthly-report/presentation/pages/MonthlyReportFormPage"
  ).then((module) => ({
    default: module.MonthlyReportFormPage,
  })),
);
const PolicyCreatePage = lazy(() =>
  import(
    "@/modules/hr/company-policy/presentation/pages/PolicyCreatePage"
  ).then((module) => ({
    default: module.PolicyCreatePage,
  })),
);
const PolicyDetailPage = lazy(() =>
  import(
    "@/modules/hr/company-policy/presentation/pages/PolicyDetailPage"
  ).then((module) => ({
    default: module.PolicyDetailPage,
  })),
);
const PolicyEditPage = lazy(() =>
  import("@/modules/hr/company-policy/presentation/pages/PolicyEditPage").then(
    (module) => ({
      default: module.PolicyEditPage,
    }),
  ),
);
const PolicyVersionsPage = lazy(() =>
  import(
    "@/modules/hr/company-policy/presentation/pages/PolicyVersionsPage"
  ).then((module) => ({
    default: module.PolicyVersionsPage,
  })),
);
const PolicyArchivePage = lazy(() =>
  import(
    "@/modules/hr/company-policy/presentation/pages/PolicyArchivePage"
  ).then((module) => ({
    default: module.PolicyArchivePage,
  })),
);
const MeetingListPage = lazy(() =>
  import(
    "@/modules/management/meeting-minutes/presentation/pages/MeetingListPage"
  ).then((module) => ({ default: module.MeetingListPage })),
);
const MeetingDetailPage = lazy(() =>
  import(
    "@/modules/management/meeting-minutes/presentation/pages/MeetingDetailPage"
  ).then((module) => ({ default: module.MeetingDetailPage })),
);
const MeetingFormPage = lazy(() =>
  import(
    "@/modules/management/meeting-minutes/presentation/pages/MeetingFormPage"
  ).then((module) => ({ default: module.MeetingFormPage })),
);
const MeetingPdfPreviewPage = lazy(() =>
  import(
    "@/modules/management/meeting-minutes/presentation/pages/MeetingPdfPreviewPage"
  ).then((module) => ({ default: module.MeetingPdfPreviewPage })),
);
const CalendarPage = lazy(() =>
  import("@/modules/management/calendar/presentation/pages/CalendarPage").then(
    (module) => ({ default: module.CalendarPage }),
  ),
);
const CalendarSettingsPage = lazy(() =>
  import(
    "@/modules/management/calendar/presentation/pages/CalendarSettingsPage"
  ).then((module) => ({ default: module.CalendarSettingsPage })),
);
const MeetingActionsPage = lazy(() =>
  import(
    "@/modules/management/meeting-minutes/presentation/pages/MeetingActionsPage"
  ).then((module) => ({ default: module.MeetingActionsPage })),
);
const PromotionRulesPage = lazy(() =>
  import("@/modules/hr/promotion/presentation/pages/PromotionRulesPage").then(
    (module) => ({
      default: module.PromotionRulesPage,
    }),
  ),
);
const PromotionRuleCreatePage = lazy(() =>
  import(
    "@/modules/hr/promotion/presentation/pages/PromotionRuleCreatePage"
  ).then((module) => ({
    default: module.PromotionRuleCreatePage,
  })),
);
const PromotionRuleEditPage = lazy(() =>
  import(
    "@/modules/hr/promotion/presentation/pages/PromotionRuleEditPage"
  ).then((module) => ({
    default: module.PromotionRuleEditPage,
  })),
);
const PromotionRecordsPage = lazy(() =>
  import("@/modules/hr/promotion/presentation/pages/PromotionRecordsPage").then(
    (module) => ({
      default: module.PromotionRecordsPage,
    }),
  ),
);
const PromotionRecordCreatePage = lazy(() =>
  import(
    "@/modules/hr/promotion/presentation/pages/PromotionRecordCreatePage"
  ).then((module) => ({
    default: module.PromotionRecordCreatePage,
  })),
);
const PromotionRecordDetailPage = lazy(() =>
  import(
    "@/modules/hr/promotion/presentation/pages/PromotionRecordDetailPage"
  ).then((module) => ({
    default: module.PromotionRecordDetailPage,
  })),
);
const PromotionRecordEditPage = lazy(() =>
  import(
    "@/modules/hr/promotion/presentation/pages/PromotionRecordEditPage"
  ).then((module) => ({
    default: module.PromotionRecordEditPage,
  })),
);
const PromotionHistoryPage = lazy(() =>
  import("@/modules/hr/promotion/presentation/pages/PromotionHistoryPage").then(
    (module) => ({
      default: module.PromotionHistoryPage,
    }),
  ),
);
const hrPayrollRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/hr/payroll",
  validateSearch: (search: Record<string, unknown>) => ({
    offset: (search.offset as number | undefined) ?? 0,
    limit: (search.limit as number | undefined) ?? 20,
    sort: parseSortParam(search.sort),
    filters: search.filters as
      | Array<{ field: string; op: string; value: unknown }>
      | undefined,
    periodYear: search.periodYear as number | undefined,
    periodMonth: search.periodMonth as number | undefined,
  }),
  component: () => (
    <RequirePermissions
      any={[AppPermissions.hr_payroll.list, AppPermissions.hr_payroll.audit]}
    >
      <LazyPage>
        <PayrollListPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const hrPayrollHistoryRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/hr/payroll/history",
  validateSearch: (search: Record<string, unknown>) => ({
    offset: (search.offset as number | undefined) ?? 0,
    limit: (search.limit as number | undefined) ?? 20,
    sort: parseSortParam(search.sort),
    periodYear: search.periodYear as number | undefined,
    periodMonth: search.periodMonth as number | undefined,
    status: (search.status as string | undefined) ?? "paid", // default paid, or __all__/draft/confirmed/paid
    paidAtFrom: search.paidAtFrom as string | undefined,
    paidAtTo: search.paidAtTo as string | undefined,
    search: search.search as string | undefined,
  }),
  component: () => (
    <RequirePermissions
      any={[AppPermissions.hr_payroll.list, AppPermissions.hr_payroll.audit]}
    >
      <LazyPage>
        <PayrollHistoryPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const _payrollPeriodCurrentYear = new Date().getFullYear();
const _payrollPeriodCurrentMonth = new Date().getMonth() + 1;
const hrPayrollPeriodRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/hr/payroll/period",
  validateSearch: (search: Record<string, unknown>) => {
    const year =
      search.year != null ? Number(search.year) : _payrollPeriodCurrentYear;
    const month =
      search.month != null ? Number(search.month) : _payrollPeriodCurrentMonth;
    return {
      year: Number.isInteger(year) ? year : _payrollPeriodCurrentYear,
      month:
        Number.isInteger(month) && month >= 1 && month <= 12
          ? month
          : _payrollPeriodCurrentMonth,
      departmentId: (search.departmentId as string) || undefined,
      status: (search.status as string) || undefined,
      search: (search.search as string) || undefined,
      sort: parseSortParam(search.sort),
    };
  },
  component: () => (
    <RequirePermissions any={[AppPermissions.hr_payroll.list]}>
      <LazyPage>
        <PayrollByPeriodPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const hrPayrollNewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/hr/payroll/period/new",
  validateSearch: (search: Record<string, unknown>) => {
    const year =
      search.year != null ? Number(search.year) : _payrollPeriodCurrentYear;
    const month =
      search.month != null ? Number(search.month) : _payrollPeriodCurrentMonth;
    return {
      year: Number.isInteger(year) ? year : _payrollPeriodCurrentYear,
      month:
        Number.isInteger(month) && month >= 1 && month <= 12
          ? month
          : _payrollPeriodCurrentMonth,
    };
  },
  component: () => (
    <RequirePermissions any={[AppPermissions.hr_payroll.create]}>
      <LazyPage>
        <PayrollNewPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const hrPayrollEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/hr/payroll/$id/edit",
  component: () => (
    <RequirePermissions
      any={[AppPermissions.hr_payroll.update, AppPermissions.hr_payroll.read]}
    >
      <LazyPage>
        <PayrollFormPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const hrPayrollSlipRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/hr/payroll/$id/slip",
  validateSearch: (search: Record<string, unknown>) => ({
    print: (search.print as number | undefined) ?? undefined,
  }),
  component: () => (
    <RequirePermissions
      any={[
        AppPermissions.hr_payroll.read,
        AppPermissions.hr_payroll.slip_download,
      ]}
    >
      <LazyPage>
        <PayslipViewPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const hrOvertimeRulesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/hr/overtime/rules",
  validateSearch: (search: Record<string, unknown>) => ({
    offset: (search.offset as number | undefined) ?? 0,
    limit: (search.limit as number | undefined) ?? 20,
    sort: parseSortParam(search.sort),
    dayType: search.dayType as "weekday" | "holiday" | undefined,
    isActive: search.isActive as boolean | undefined,
    search: search.search as string | undefined,
  }),
  component: () => (
    <RequirePermissions
      any={[
        AppPermissions.hr_overtime.rules_read,
        AppPermissions.hr_overtime.rules_update,
      ]}
    >
      <LazyPage>
        <OTRulesPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const hrOvertimeRequestsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/hr/overtime/requests",
  validateSearch: (search: Record<string, unknown>) => ({
    offset: (search.offset as number | undefined) ?? 0,
    limit: (search.limit as number | undefined) ?? 20,
    sort: parseSortParam(search.sort),
    status: search.status as string | undefined,
    search: search.search as string | undefined,
    mine: search.mine as boolean | undefined,
  }),
  component: () => (
    <RequirePermissions any={[AppPermissions.hr_overtime.request_list]}>
      <LazyPage>
        <OTRequestListPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const hrOvertimeRulesNewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/hr/overtime/rules/new",
  component: () => (
    <RequirePermissions
      any={[
        AppPermissions.hr_overtime.rules_create,
        AppPermissions.hr_overtime.rules_update,
      ]}
    >
      <LazyPage>
        <OTRuleCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const hrOvertimeRuleDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/hr/overtime/rules/$id",
  component: () => (
    <RequirePermissions
      any={[
        AppPermissions.hr_overtime.rules_read,
        AppPermissions.hr_overtime.rules_update,
      ]}
    >
      <LazyPage>
        <OTRuleDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const hrOvertimeRuleEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/hr/overtime/rules/$id/edit",
  component: () => (
    <RequirePermissions any={[AppPermissions.hr_overtime.rules_update]}>
      <LazyPage>
        <OTRuleEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const hrOvertimeRequestsNewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/hr/overtime/requests/new",
  component: () => (
    <RequirePermissions any={[AppPermissions.hr_overtime.request_create]}>
      <LazyPage>
        <OTRequestFormPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const hrOvertimeRequestsNewForEmployeeRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/hr/overtime/requests/new-for-employee",
  validateSearch: (search: Record<string, unknown>) => ({
    employeeId: search.employeeId as string | undefined,
  }),
  component: () => (
    <RequirePermissions any={[AppPermissions.hr_overtime.approve]}>
      <LazyPage>
        <OTRequestFormForEmployeePage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const hrOvertimeRequestDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/hr/overtime/requests/$id",
  validateSearch: (search: Record<string, unknown>) => ({
    from: search.from as string | undefined,
    offset: search.offset as number | undefined,
    limit: search.limit as number | undefined,
    sort: parseSortParam(search.sort),
    periodYear: search.periodYear as number | undefined,
    periodMonth: search.periodMonth as number | undefined,
    status: search.status as string | undefined,
    search: search.search as string | undefined,
    employeeId: search.employeeId as string | undefined,
  }),
  component: () => (
    <RequirePermissions any={[AppPermissions.hr_overtime.request_list]}>
      <LazyPage>
        <OTRequestDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const hrOvertimeApprovalsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/hr/overtime/approvals",
  validateSearch: (search: Record<string, unknown>) => ({
    offset: (search.offset as number | undefined) ?? 0,
    limit: (search.limit as number | undefined) ?? 20,
    sort: parseSortParam(search.sort),
    periodYear: search.periodYear as number | undefined,
    periodMonth: search.periodMonth as number | undefined,
    status: search.status as string | undefined,
    search: search.search as string | undefined,
    employeeId: search.employeeId as string | undefined,
  }),
  component: () => (
    <RequirePermissions any={[AppPermissions.hr_overtime.approve]}>
      <LazyPage>
        <OTApprovalListPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const hrOvertimeSummaryRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/hr/overtime/summary",
  validateSearch: (search: Record<string, unknown>) => ({
    year: search.year as number | undefined,
    month: search.month as number | undefined,
    sort: parseSortParam(search.sort),
  }),
  component: () => (
    <RequirePermissions
      any={[
        AppPermissions.hr_overtime.summary,
        AppPermissions.hr_overtime.list,
      ]}
    >
      <LazyPage>
        <OTSummaryPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const hrLeaveTypesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/hr/leave/types",
  validateSearch: (search: Record<string, unknown>) => ({
    offset: (search.offset as number | undefined) ?? 0,
    limit: (search.limit as number | undefined) ?? 20,
    sort: parseSortParam(search.sort),
    isActive: search.isActive as boolean | undefined,
    search: search.search as string | undefined,
  }),
  component: () => (
    <RequirePermissions
      any={[
        AppPermissions.hr_leave.types_read,
        AppPermissions.hr_leave.types_update,
      ]}
    >
      <LazyPage>
        <LeaveTypesPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const hrLeaveTypesNewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/hr/leave/types/new",
  component: () => (
    <RequirePermissions
      any={[
        AppPermissions.hr_leave.types_create,
        AppPermissions.hr_leave.types_update,
      ]}
    >
      <LazyPage>
        <LeaveTypeCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const hrLeaveTypeDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/hr/leave/types/$id",
  component: () => (
    <RequirePermissions
      any={[
        AppPermissions.hr_leave.types_read,
        AppPermissions.hr_leave.types_update,
      ]}
    >
      <LazyPage>
        <LeaveTypeDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const hrLeaveTypeEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/hr/leave/types/$id/edit",
  component: () => (
    <RequirePermissions any={[AppPermissions.hr_leave.types_update]}>
      <LazyPage>
        <LeaveTypeEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const currentYear = new Date().getFullYear();
const hrLeaveEntitlementsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/hr/leave/entitlements",
  validateSearch: (search: Record<string, unknown>) => ({
    offset: (search.offset as number | undefined) ?? 0,
    limit: (search.limit as number | undefined) ?? 20,
    sort: parseSortParam(search.sort),
    year: (search.year as number | undefined) ?? currentYear,
    leaveTypeId: search.leaveTypeId as string | undefined,
    search: search.search as string | undefined,
  }),
  component: () => (
    <RequirePermissions
      any={[
        AppPermissions.hr_leave.entitlements_read,
        AppPermissions.hr_leave.entitlements_update,
      ]}
    >
      <LazyPage>
        <LeaveEntitlementsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const hrLeaveEntitlementsNewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/hr/leave/entitlements/new",
  component: () => (
    <RequirePermissions
      any={[
        AppPermissions.hr_leave.entitlements_create,
        AppPermissions.hr_leave.entitlements_read,
      ]}
    >
      <LazyPage>
        <LeaveEntitlementCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const hrLeaveEntitlementByEmployeeRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/hr/leave/entitlements/employee/$employeeId",
  validateSearch: (search: Record<string, unknown>) => ({
    year: (search.year as number | undefined) ?? currentYear,
  }),
  component: () => (
    <RequirePermissions
      any={[
        AppPermissions.hr_leave.entitlements_read,
        AppPermissions.hr_leave.entitlements_update,
        AppPermissions.hr_leave.entitlements_create,
      ]}
    >
      <LazyPage>
        <LeaveEntitlementByEmployeePage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const currentMonth = new Date().getMonth() + 1;
const hrLeaveApprovalsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/hr/leave/approvals",
  validateSearch: (search: Record<string, unknown>) => ({
    offset: (search.offset as number | undefined) ?? 0,
    limit: (search.limit as number | undefined) ?? 20,
    sort: parseSortParam(search.sort),
    periodYear: (search.periodYear as number | undefined) ?? currentYear,
    periodMonth: (search.periodMonth as number | undefined) ?? currentMonth,
    periodFromOnly:
      search.periodFromOnly !== false && search.periodFromOnly !== "false",
    filterByCreated:
      search.filterByCreated !== false && search.filterByCreated !== "false",
    status: search.status as string | undefined,
    search: search.search as string | undefined,
  }),
  component: () => (
    <RequirePermissions
      any={[
        AppPermissions.hr_leave.approve,
        AppPermissions.hr_leave.approve_line,
      ]}
    >
      <LazyPage>
        <LeaveApprovalsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const hrLeaveHistoryRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/hr/leave/history",
  validateSearch: (search: Record<string, unknown>) => ({
    offset: (search.offset as number | undefined) ?? 0,
    limit: (search.limit as number | undefined) ?? 20,
    sort: parseSortParam(search.sort),
    periodYear: (search.periodYear as number | undefined) ?? currentYear,
    periodMonth: (search.periodMonth as number | undefined) ?? currentMonth,
    periodFromOnly:
      search.periodFromOnly === true || search.periodFromOnly === "true",
    status: search.status as string | undefined,
    search: search.search as string | undefined,
  }),
  component: () => (
    <RequirePermissions any={[AppPermissions.hr_leave.history]}>
      <LazyPage>
        <LeaveHistoryPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const hrLeaveRequestNewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/hr/leave/requests/new",
  component: () => (
    <RequirePermissions any={[AppPermissions.hr_leave.request_create]}>
      <LazyPage>
        <LeaveRequestCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const hrLeaveRequestDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/hr/leave/requests/$id",
  validateSearch: (search: Record<string, unknown>) => ({
    from: search.from as string | undefined,
    offset: (search.offset as number | undefined) ?? 0,
    limit: (search.limit as number | undefined) ?? 20,
    periodYear: (search.periodYear as number | undefined) ?? currentYear,
    periodMonth: (search.periodMonth as number | undefined) ?? currentMonth,
    status: search.status as string | undefined,
    search: search.search as string | undefined,
  }),
  component: () => (
    <RequirePermissions
      any={[
        AppPermissions.hr_leave.request_create,
        AppPermissions.hr_leave.approve,
        AppPermissions.hr_leave.approve_line,
        AppPermissions.hr_leave.history,
      ]}
    >
      <LazyPage>
        <LeaveRequestDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const hrLeaveRequestEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/hr/leave/requests/$id/edit",
  validateSearch: (search: Record<string, unknown>) => ({
    from: search.from as string | undefined,
    offset: (search.offset as number | undefined) ?? 0,
    limit: (search.limit as number | undefined) ?? 20,
    periodYear: (search.periodYear as number | undefined) ?? currentYear,
    periodMonth: (search.periodMonth as number | undefined) ?? currentMonth,
    status: search.status as string | undefined,
    search: search.search as string | undefined,
  }),
  component: () => (
    <RequirePermissions any={[AppPermissions.hr_leave.request_update]}>
      <LazyPage>
        <LeaveRequestEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const managementProjectsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/management/projects",
  validateSearch: (search: Record<string, unknown>) => ({
    offset: (search.offset as number | undefined) ?? 0,
    limit: (search.limit as number | undefined) ?? 20,
    sort: parseSortParam(search.sort),
    status: search.status as string | undefined,
    level: search.level as
      | "position"
      | "department"
      | "organization"
      | undefined,
    departmentId: search.departmentId as string | undefined,
    positionId: search.positionId as string | undefined,
    responsibleUserId: search.responsibleUserId as string | undefined,
    startDateFrom: search.startDateFrom as string | undefined,
    startDateTo: search.startDateTo as string | undefined,
    endDateFrom: search.endDateFrom as string | undefined,
    endDateTo: search.endDateTo as string | undefined,
    search: search.search as string | undefined,
  }),
  component: () => (
    <RequirePermissions
      any={[AppPermissions.mgmt_project.read, AppPermissions.mgmt_project.list]}
    >
      <LazyPage>
        <ProjectListPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const managementProjectSummaryRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/management/projects/summary",
  validateSearch: (search: Record<string, unknown>) => ({
    departmentId: search.departmentId as string | undefined,
    startDateFrom: search.startDateFrom as string | undefined,
    startDateTo: search.startDateTo as string | undefined,
    level: search.level as
      | "position"
      | "department"
      | "organization"
      | undefined,
  }),
  component: () => (
    <RequirePermissions
      any={[AppPermissions.mgmt_project.read, AppPermissions.mgmt_project.list]}
    >
      <LazyPage>
        <ProjectSummaryPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const managementProjectDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/management/projects/$id",
  component: () => (
    <RequirePermissions any={[AppPermissions.mgmt_project.read]}>
      <LazyPage>
        <ProjectDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const managementProjectEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/management/projects/$id/edit",
  component: () => (
    <RequirePermissions any={[AppPermissions.mgmt_project.update]}>
      <LazyPage>
        <ProjectFormPage mode="edit" />
      </LazyPage>
    </RequirePermissions>
  ),
});
const managementProjectNewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/management/projects/new",
  component: () => (
    <RequirePermissions any={[AppPermissions.mgmt_project.create]}>
      <LazyPage>
        <ProjectFormPage mode="create" />
      </LazyPage>
    </RequirePermissions>
  ),
});
const managementMonthlyReportsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/management/monthly-reports",
  validateSearch: (search: Record<string, unknown>) => ({
    offset: (search.offset as number | undefined) ?? 0,
    limit: (search.limit as number | undefined) ?? 20,
    sort: parseSortParam(search.sort),
    periodYear: search.periodYear as number | undefined,
    periodMonth: search.periodMonth as number | undefined,
    departmentId: search.departmentId as string | undefined,
    search: search.search as string | undefined,
  }),
  component: () => (
    <RequirePermissions
      any={[
        AppPermissions.mgmt_monthly_report.read,
        AppPermissions.mgmt_monthly_report.list,
      ]}
    >
      <LazyPage>
        <MonthlyReportListPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const managementMonthlyReportDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/management/monthly-reports/$id",
  component: () => (
    <RequirePermissions any={[AppPermissions.mgmt_monthly_report.read]}>
      <LazyPage>
        <MonthlyReportDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const managementMonthlyReportEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/management/monthly-reports/$id/edit",
  component: () => (
    <RequirePermissions any={[AppPermissions.mgmt_monthly_report.update]}>
      <LazyPage>
        <MonthlyReportFormPage mode="edit" />
      </LazyPage>
    </RequirePermissions>
  ),
});
const managementMonthlyReportNewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/management/monthly-reports/new",
  component: () => (
    <RequirePermissions any={[AppPermissions.mgmt_monthly_report.create]}>
      <LazyPage>
        <MonthlyReportFormPage mode="create" />
      </LazyPage>
    </RequirePermissions>
  ),
});
const hrCompanyPolicyRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/hr/company-policy",
  validateSearch: (search: Record<string, unknown>) => ({
    offset: (search.offset as number | undefined) ?? 0,
    limit: (search.limit as number | undefined) ?? 20,
    sort: parseSortParam(search.sort),
    policyTypeId: search.policyTypeId as string | undefined,
    status: search.status as string | undefined,
    search: search.search as string | undefined,
  }),
  component: () => (
    <RequirePermissions any={[AppPermissions.hr_company_policy.read]}>
      <LazyPage>
        <PolicyListPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const hrCompanyPolicyNewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/hr/company-policy/new",
  validateSearch: (search: Record<string, unknown>) => ({
    duplicateFrom: search.duplicateFrom as string | undefined,
  }),
  component: () => (
    <RequirePermissions any={[AppPermissions.hr_company_policy.create]}>
      <LazyPage>
        <PolicyCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const hrCompanyPolicyArchiveRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/hr/company-policy/archive",
  validateSearch: (search: Record<string, unknown>) => ({
    offset: (search.offset as number | undefined) ?? 0,
    limit: (search.limit as number | undefined) ?? 20,
    sort: parseSortParam(search.sort),
    policyTypeId: search.policyTypeId as string | undefined,
    search: search.search as string | undefined,
  }),
  component: () => (
    <RequirePermissions any={[AppPermissions.hr_company_policy.read]}>
      <LazyPage>
        <PolicyArchivePage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const hrCompanyPolicyDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/hr/company-policy/$id",
  component: () => (
    <RequirePermissions any={[AppPermissions.hr_company_policy.read]}>
      <LazyPage>
        <PolicyDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const hrCompanyPolicyEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/hr/company-policy/$id/edit",
  component: () => (
    <RequirePermissions any={[AppPermissions.hr_company_policy.update]}>
      <LazyPage>
        <PolicyEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const hrCompanyPolicyVersionsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/hr/company-policy/$id/versions",
  component: () => (
    <RequirePermissions any={[AppPermissions.hr_company_policy.read]}>
      <LazyPage>
        <PolicyVersionsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const hrPromotionRulesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/hr/promotion/rules",
  validateSearch: (search: Record<string, unknown>) => ({
    offset: (search.offset as number | undefined) ?? 0,
    limit: (search.limit as number | undefined) ?? 20,
    sort: parseSortParam(search.sort),
    isActive: search.isActive as boolean | undefined,
    fromPositionId: search.fromPositionId as string | undefined,
    toPositionId: search.toPositionId as string | undefined,
    search: search.search as string | undefined,
  }),
  component: () => (
    <RequirePermissions
      any={[
        AppPermissions.hr_promotion.rule_read,
        AppPermissions.hr_promotion.rule_update,
      ]}
    >
      <LazyPage>
        <PromotionRulesPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const hrPromotionRulesNewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/hr/promotion/rules/new",
  component: () => (
    <RequirePermissions
      any={[
        AppPermissions.hr_promotion.rule_create,
        AppPermissions.hr_promotion.rule_update,
      ]}
    >
      <LazyPage>
        <PromotionRuleCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const hrPromotionRuleEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/hr/promotion/rules/$id/edit",
  component: () => (
    <RequirePermissions any={[AppPermissions.hr_promotion.rule_update]}>
      <LazyPage>
        <PromotionRuleEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const hrPromotionRecordsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/hr/promotion/records",
  validateSearch: (search: Record<string, unknown>) => ({
    offset: (search.offset as number | undefined) ?? 0,
    limit: (search.limit as number | undefined) ?? 20,
    sort: parseSortParam(search.sort),
    employeeId: search.employeeId as string | undefined,
    periodYear: search.periodYear as number | undefined,
    periodMonth: search.periodMonth as number | undefined,
    positionId: search.positionId as string | undefined,
    search: search.search as string | undefined,
  }),
  component: () => (
    <RequirePermissions any={[AppPermissions.hr_promotion.record_read]}>
      <LazyPage>
        <PromotionRecordsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const hrPromotionRecordsNewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/hr/promotion/records/new",
  component: () => (
    <RequirePermissions
      any={[
        AppPermissions.hr_promotion.record_create,
        AppPermissions.hr_promotion.approve,
      ]}
    >
      <LazyPage>
        <PromotionRecordCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const hrPromotionRecordDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/hr/promotion/records/$id",
  component: () => (
    <RequirePermissions any={[AppPermissions.hr_promotion.record_read]}>
      <LazyPage>
        <PromotionRecordDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const hrPromotionRecordEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/hr/promotion/records/$id/edit",
  component: () => (
    <RequirePermissions any={[AppPermissions.hr_promotion.record_update]}>
      <LazyPage>
        <PromotionRecordEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const hrPromotionHistoryRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/hr/promotion/history",
  validateSearch: (search: Record<string, unknown>) => ({
    offset: (search.offset as number | undefined) ?? 0,
    limit: (search.limit as number | undefined) ?? 20,
    sort: parseSortParam(search.sort),
    employeeId: search.employeeId as string | undefined,
    periodYear: search.periodYear as number | undefined,
    periodMonth: search.periodMonth as number | undefined,
    positionId: search.positionId as string | undefined,
    search: search.search as string | undefined,
  }),
  component: () => (
    <RequirePermissions
      any={[
        AppPermissions.hr_promotion.history,
        AppPermissions.hr_promotion.record_read,
      ]}
    >
      <LazyPage>
        <PromotionHistoryPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const managementMeetingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/management/meetings",
  validateSearch: (search: Record<string, unknown>) => ({
    offset: (search.offset as number | undefined) ?? 0,
    limit: (search.limit as number | undefined) ?? 20,
    sort: parseSortParam(search.sort),
    dateFrom: search.dateFrom as string | undefined,
    dateTo: search.dateTo as string | undefined,
    search: search.search as string | undefined,
  }),
  component: () => (
    <RequirePermissions
      any={[AppPermissions.mgmt_meeting.list, AppPermissions.mgmt_meeting.read]}
    >
      <LazyPage>
        <MeetingListPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const managementMeetingActionsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/management/meetings/actions",
  validateSearch: (search: Record<string, unknown>) => ({
    offset: (search.offset as number | undefined) ?? 0,
    limit: (search.limit as number | undefined) ?? 20,
    sort: parseSortParam(search.sort),
    dueDateFrom: search.dueDateFrom as string | undefined,
    dueDateTo: search.dueDateTo as string | undefined,
    status: search.status as "pending" | "done" | undefined,
    assigneeId: search.assigneeId as string | undefined,
    meetingId: search.meetingId as string | undefined,
  }),
  component: () => (
    <RequirePermissions any={[AppPermissions.mgmt_meeting.read]}>
      <LazyPage>
        <MeetingActionsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const managementMeetingsNewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/management/meetings/new",
  component: () => (
    <RequirePermissions any={[AppPermissions.mgmt_meeting.create]}>
      <LazyPage>
        <MeetingFormPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const managementMeetingDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/management/meetings/$id",
  component: () => (
    <RequirePermissions any={[AppPermissions.mgmt_meeting.read]}>
      <LazyPage>
        <MeetingDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const managementMeetingEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/management/meetings/$id/edit",
  component: () => (
    <RequirePermissions any={[AppPermissions.mgmt_meeting.update]}>
      <LazyPage>
        <MeetingFormPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const managementMeetingPdfPreviewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/management/meetings/$id/pdf-preview",
  component: () => (
    <RequirePermissions any={[AppPermissions.mgmt_meeting.read]}>
      <LazyPage>
        <MeetingPdfPreviewPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const managementCalendarRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/management/calendar",
  component: () => (
    <RequirePermissions any={[AppPermissions.mgmt_calendar.read]}>
      <LazyPage>
        <CalendarPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const managementCalendarSettingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/management/calendar/settings",
  component: () => (
    <RequirePermissions any={[AppPermissions.mgmt_calendar.settings]}>
      <LazyPage>
        <CalendarSettingsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const managementOkrKpiRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/management/okr-kpi",
  component: () => (
    <RequirePermissions
      any={[AppPermissions.mgmt_okr_kpi.read, AppPermissions.kpi.read]}
    >
      <LazyPage>
        <KPIOverviewPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const managementOkrKpiCalculationRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/management/okr-kpi/calculation",
  component: () => (
    <RequirePermissions
      any={[AppPermissions.mgmt_okr_kpi.read, AppPermissions.kpi.read]}
    >
      <LazyPage>
        <KPICalculationPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const managementOkrKpiExplainRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/management/okr-kpi/explain",
  validateSearch: (search: Record<string, unknown>) =>
    parseKpiOverviewExplainSearch(search),
  component: () => (
    <RequirePermissions
      any={[AppPermissions.mgmt_okr_kpi.read, AppPermissions.kpi.read]}
    >
      <LazyPage>
        <KPIOverviewExplainPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const hrEmployeesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/hr/employees",
  validateSearch: (search: Record<string, unknown>) => ({
    offset: (search.offset as number | undefined) ?? 0,
    limit: (search.limit as number | undefined) ?? 20,
    search: (search.search as string | undefined) ?? undefined,
    branch: (search.branch as string | undefined) ?? undefined,
    status: (search.status as string | undefined) ?? undefined,
    departmentId: (search.departmentId as string | undefined) ?? undefined,
  }),
  component: () => (
    <RequirePermissions any={[AppPermissions.hr_employee_profile.list]}>
      <LazyPage>
        <EmployeeProfileListPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const hrEmployeeDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/hr/employees/$userId",
  component: () => (
    <RequirePermissions
      any={[
        AppPermissions.hr_employee_profile.read,
        AppPermissions.hr_employee_profile.upsert,
      ]}
    >
      <LazyPage>
        <EmployeeProfileDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const hrSalaryAdvanceRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/hr/salary-advance",
  validateSearch: (search: Record<string, unknown>) => ({
    offset: (search.offset as number | undefined) ?? 0,
    limit: (search.limit as number | undefined) ?? 20,
    sort: parseSortParam(search.sort),
    status: (search.status as string | undefined) ?? undefined,
    employeeId: (search.employeeId as string | undefined) ?? undefined,
  }),
  component: () => (
    <RequirePermissions
      any={[
        AppPermissions.hr_salary_advance.list,
        AppPermissions.hr_salary_advance.approve,
      ]}
    >
      <LazyPage>
        <SalaryAdvanceListPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const hrSalaryAdvanceNewForEmployeeRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/hr/salary-advance/new",
  component: () => (
    <RequirePermissions any={[AppPermissions.hr_salary_advance.approve]}>
      <LazyPage>
        <NewAdvanceForEmployeePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const hrSalaryAdvanceDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/hr/salary-advance/$id",
  component: () => (
    <RequirePermissions any={[AppPermissions.hr_salary_advance.read]}>
      <LazyPage>
        <SalaryAdvanceDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const hrMyAdvanceListRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/hr/salary-advance/requests",
  validateSearch: (search: Record<string, unknown>) => ({
    offset: (search.offset as number | undefined) ?? 0,
    limit: (search.limit as number | undefined) ?? 20,
    sort: parseSortParam(search.sort),
    status: (search.status as string | undefined) ?? undefined,
  }),
  component: () => (
    <RequirePermissions
      any={[AppPermissions.hr_salary_advance.request_read_self]}
    >
      <LazyPage>
        <MyAdvanceListPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const hrMyAdvanceNewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/hr/salary-advance/requests/new",
  component: () => (
    <RequirePermissions any={[AppPermissions.hr_salary_advance.request_create]}>
      <LazyPage>
        <NewAdvanceRequestPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const hrMyAdvanceDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/hr/salary-advance/requests/$id",
  component: () => (
    <RequirePermissions
      any={[AppPermissions.hr_salary_advance.request_read_self]}
    >
      <LazyPage>
        <MyAdvanceDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

export const routeTree = rootRoute.addChildren([
  authLayoutRoute.addChildren([loginRoute]),
  appRoute.addChildren([
    dashboardRoute,
    rolesRoute,
    roleCreateRoute,
    roleEditRoute,
    usersRoute,
    userProfileRoute,
    positionsRoute,
    departmentsRoute,
    sopRoute,
    sopCreateRoute,
    sopEditRoute,
    sopDetailRoute,
    sopPdfPreviewRoute,
    sopMappingRoute,
    orgChartRoute,
    assignPositionRoute,
    // auditRoute,
    // auditDetailRoute,
    profileRoute,
    jdRoute,
    jdDetailRoute,
    jdPdfPreviewRoute,
    jdCreateRoute,
    jdEditRoute,
    jdMappingRoute,
    checklistsRoute,
    checklistExecutionHistoryRoute,
    checklistResultRoute,
    checklistExecutionDetailRoute,
    checklistCreateRoute,
    checklistDetailRoute,
    checklistEditRoute,
    flowsRoute,
    flowDetailRoute,
    flowEditRoute,
    qcRoute,
    qcDetailRoute,
    qcPdfPreviewRoute,
    qcCreateRoute,
    qcEditRoute,
    qcRecordListRoute,
    qcRecordRoute,
    qcRecordEditRoute,
    qcRecordDetailRoute,
    qcRecordPdfPreviewRoute,
    qcResultRoute,
    kpiRoute,
    kpiCalculationRoute,
    kpiDetailRoute,
    kpisRoute,
    kpiCreateRoute,
    kpiEditRoute,
    kpiPdfPreviewRoute,
    serviceStandardRoute,
    serviceStandardDetailRoute,
    serviceStandardCreateRoute,
    serviceStandardEditRoute,
    serviceStandardPdfPreviewRoute,
    serviceStandardMappingRoute,
    internalAuditRoute,
    internalAuditPdfPreviewRoute,
    internalAuditDetailRoute,
    internalAuditCreateRoute,
    internalAuditEditRoute,
    internalAuditRecordListRoute,
    internalAuditRecordNewRoute,
    internalAuditRecordCreateRoute,
    internalAuditRecordEditRoute,
    internalAuditRecordPdfPreviewRoute,
    internalAuditRecordDetailRoute,
    internalAuditIssueListRoute,
    internalAuditSummaryRoute,
    templateRoute,
    templateDetailRoute,
    templateCreateRoute,
    templateEditRoute,
    templateByDepartmentRoute,
    formsRoute,
    formCreateRoute,
    formDetailRoute,
    formEditRoute,
    formAnalysisRoute,
    formPreviewRoute,
    formSubmissionsRoute,
    formSubmissionDetailRoute,
    categoriesRoute,
    categoryCreateRoute,
    categoryEditRoute,
    contractsRoute,
    contractCreateRoute,
    contractEditRoute,
    contractPdfPreviewRoute,
    contractDetailRoute,
    contractExpiryRoute,
    documentsRoute,
    documentsSettingsRoute,
    documentsAuditRoute,
    documentsDetailRoute,
    documentsPdfPreviewRoute,
    documentsNewRoute,
    documentsEditRoute,
    hrPayrollHistoryRoute,
    hrPayrollPeriodRoute,
    hrPayrollNewRoute,
    hrPayrollEditRoute,
    hrPayrollSlipRoute,
    hrPayrollRoute,
    hrOvertimeRulesRoute,
    hrOvertimeRequestsRoute,
    hrOvertimeApprovalsRoute,
    hrOvertimeSummaryRoute,
    hrOvertimeRulesNewRoute,
    hrOvertimeRuleDetailRoute,
    hrOvertimeRuleEditRoute,
    hrOvertimeRequestsNewRoute,
    hrOvertimeRequestsNewForEmployeeRoute,
    hrOvertimeRequestDetailRoute,
    hrLeaveTypesRoute,
    hrLeaveTypesNewRoute,
    hrLeaveTypeDetailRoute,
    hrLeaveTypeEditRoute,
    hrLeaveEntitlementsRoute,
    hrLeaveEntitlementsNewRoute,
    hrLeaveEntitlementByEmployeeRoute,
    hrLeaveApprovalsRoute,
    hrLeaveHistoryRoute,
    hrLeaveRequestNewRoute,
    hrLeaveRequestDetailRoute,
    hrLeaveRequestEditRoute,
    managementProjectsRoute,
    managementProjectSummaryRoute,
    managementProjectDetailRoute,
    managementProjectEditRoute,
    managementProjectNewRoute,
    managementMonthlyReportsRoute,
    managementMonthlyReportDetailRoute,
    managementMonthlyReportEditRoute,
    managementMonthlyReportNewRoute,
    hrCompanyPolicyRoute,
    hrCompanyPolicyNewRoute,
    hrCompanyPolicyArchiveRoute,
    hrCompanyPolicyDetailRoute,
    hrCompanyPolicyEditRoute,
    hrCompanyPolicyVersionsRoute,
    hrPromotionRulesRoute,
    hrPromotionRulesNewRoute,
    hrPromotionRuleEditRoute,
    hrPromotionRecordsRoute,
    hrPromotionRecordsNewRoute,
    hrPromotionRecordDetailRoute,
    hrPromotionRecordEditRoute,
    hrPromotionHistoryRoute,
    hrEmployeesRoute,
    hrEmployeeDetailRoute,
    hrSalaryAdvanceRoute,
    hrSalaryAdvanceNewForEmployeeRoute,
    hrSalaryAdvanceDetailRoute,
    hrMyAdvanceListRoute,
    hrMyAdvanceNewRoute,
    hrMyAdvanceDetailRoute,
    managementMeetingsRoute,
    managementMeetingActionsRoute,
    managementMeetingsNewRoute,
    managementMeetingDetailRoute,
    managementMeetingEditRoute,
    managementMeetingPdfPreviewRoute,
    managementCalendarRoute,
    managementCalendarSettingsRoute,
    managementOkrKpiRoute,
    managementOkrKpiCalculationRoute,
    managementOkrKpiExplainRoute,
  ]),
  forbiddenRoute,
]);
export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
